import './TopBar.scss';

import { SignedInItems } from '@casa/kaoto-extension/auth';
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  Masthead,
  MastheadBrand,
  MastheadContent,
  MastheadLogo,
  MastheadMain,
  MastheadProps,
  MastheadToggle,
  MenuToggle,
  MenuToggleElement,
  ToolbarItem,
} from '@patternfly/react-core';
import { EllipsisVIcon } from '@patternfly/react-icons';
import { BarsIcon } from '@patternfly/react-icons/dist/js/icons/bars-icon';
import React, { FunctionComponent, useCallback } from 'react';
import { Link } from 'react-router-dom';

import { useComponentLink } from '../hooks/ComponentLink';
import { DEFAULT_POPPER_PROPS } from '../models/popper-default';
import { Links } from '../router/links.models';
import { KaotoAboutModal } from './KaotoAboutModal';

interface ITopBar {
  navToggle: () => void;
}

const displayObject: MastheadProps['display'] = {
  default: 'inline',
};

const TopBarMenuToggle: FunctionComponent<{
  toggleRef: React.Ref<MenuToggleElement>;
  onClick: () => void;
  isOpen: boolean;
}> = ({ toggleRef, onClick, isOpen }) => (
  <MenuToggle ref={toggleRef} onClick={onClick} variant="plain" isExpanded={isOpen}>
    <EllipsisVIcon />
  </MenuToggle>
);

export const TopBar: FunctionComponent<ITopBar> = (props) => {
  const logoLink = useComponentLink(Links.Home);
  const [isOpen, setIsOpen] = React.useState(false);
  const [isAboutModalOpen, setIsAboutModalOpen] = React.useState(false);

  const onToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const onSelect = (event: React.MouseEvent<Element, MouseEvent> | undefined) => {
    event?.stopPropagation();
    setIsOpen(!isOpen);
  };

  const toggleAboutModal = () => {
    setIsAboutModalOpen(!isAboutModalOpen);
  };

  const renderToggle = useCallback(
    (toggleRef: React.Ref<MenuToggleElement>) => (
      <TopBarMenuToggle toggleRef={toggleRef} onClick={onToggle} isOpen={isOpen} />
    ),
    [onToggle, isOpen],
  );

  return (
    <>
      <Masthead id="stack-inline-masthead" display={displayObject}>
        <MastheadMain>
          <MastheadToggle>
            <Button icon={<BarsIcon />} variant="plain" onClick={props.navToggle} aria-label="Global navigation" />
          </MastheadToggle>
          <MastheadBrand data-codemods>
            <MastheadLogo data-codemods component={logoLink} className="masthead-logo">
              <span className="shell__wordmark">Casa</span>
            </MastheadLogo>
          </MastheadBrand>
        </MastheadMain>

        <MastheadContent>
          <ToolbarItem className="shell__link">
            <Dropdown
              onSelect={onSelect}
              toggle={renderToggle}
              isOpen={isOpen}
              onOpenChange={(isOpen: boolean) => {
                setIsOpen(isOpen);
              }}
              popperProps={DEFAULT_POPPER_PROPS}
            >
              <SignedInItems />
              <Divider component="li" key="separator2" />
              <Link data-testid="settings-link" to={Links.Settings}>
                <DropdownItem id="settings" key="settings">
                  Settings
                </DropdownItem>
              </Link>
              <Divider component="li" key="separator3" />
              <DropdownItem
                id="about"
                key="about"
                onClick={() => {
                  toggleAboutModal();
                }}
              >
                About
              </DropdownItem>
            </Dropdown>
          </ToolbarItem>
        </MastheadContent>
      </Masthead>

      <KaotoAboutModal
        handleCloseModal={() => {
          toggleAboutModal();
        }}
        isModalOpen={isAboutModalOpen}
      />
    </>
  );
};
